import '@babylonjs/core/Meshes/thinInstanceMesh';
import type { Board } from '@archon3d/core';
import type { Camera } from '@babylonjs/core/Cameras/camera';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Matrix, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { VertexBuffer } from '@babylonjs/core/Buffers/buffer';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';
import type { CellTarget } from '../../game/session';
import { FONT, roundedRect } from '../tablets/tabletCanvas';
import { BOARD_TOP_Y, COLORS, TILE_SIZE } from './constants';
import { createFloatingLabels, type SlotRect } from './floatingLabels';
import { cellToWorld } from './layout';
import { OVERHEAD_GAP, OVERHEAD_GROW } from './overhead';
import { PALETTES, type Palette } from './palette';
import { oddsColor, oddsCss, oddsLabel } from './odds';
import { DEFAULT_PIECE_TOP, SIGN_REACH } from './statusSigns';

/** A cell the player may pick; an attack may carry the attacker's chance of winning there. */
export interface RingTarget extends CellTarget {
  odds?: number;
}

/** The piece on a labelled cell, as a label over it needs to know it. */
export interface Standing {
  /** How far its top stands over the board. */
  top: number;
  /** Whether a status sign is over it already. */
  signed: boolean;
}

export interface Highlights {
  /**
   * Rings on cells the player may pick; attacks with odds are coloured and labelled by them. The
   * label stands over the piece on the cell, as `standing` says it is: its top over the board,
   * and whether a sign over it is already there to stand above.
   */
  setTargets(
    targets: RingTarget[],
    pieceCells: { x: number; y: number }[],
    standing?: (x: number, y: number) => Standing | undefined,
  ): void;
  /** Ring under the selected piece, or none. */
  setSelection(cell: { x: number; y: number } | null): void;
  /** Ring under the cell the cursor rests on (the mouse, or a gamepad's steps), or none. */
  setHover(cell: { x: number; y: number } | null): void;
  /** Recolours the rings, those showing now included. */
  setPalette(palette: Palette): void;
  /** Thin warning rings under pieces an enemy could capture next move and would gain by. */
  setThreats(cells: { x: number; y: number }[]): void;
  /** Pale rings under pieces that are frozen, cursed or imprisoned: the sign overhead names which. */
  setAfflicted(cells: { x: number; y: number }[]): void;
  /** The squares the last move went from and to, faintly lit; none clears them. */
  setLastMove(cells: { x: number; y: number }[]): void;
  /** Turns the odds labels to face the eye and pulses the warnings; call every frame. */
  update(dtSeconds: number, camera: Camera | null): void;
  /** What the labels above the attack targets say, for tests. */
  labels(): string[];
  dispose(): void;
}

/** The odds labels share one canvas: a grid of slots, one per label, on a single quad mesh. */
const ATLAS_WIDTH = 1024;
const ATLAS_HEIGHT = 512;
const SLOT_WIDTH = 128;
const SLOT_HEIGHT = 64;
const SLOTS_PER_ROW = ATLAS_WIDTH / SLOT_WIDTH;
const SLOT_COUNT = SLOTS_PER_ROW * (ATLAS_HEIGHT / SLOT_HEIGHT);
/** Label size in metres, the same 2 : 1 as a slot. */
/** How strongly the last move's squares are lit: enough to find, too faint to read as a ring. */
const LAST_MOVE_ALPHA = 0.38;
const LABEL_WIDTH = TILE_SIZE * 0.6;
const LABEL_HEIGHT = TILE_SIZE * 0.3;

/**
 * Thin-instanced rings: one draw call for all targets, one for the selection, one for the
 * warnings. The odds above attack targets are one more, a sheet of labels that face the eye
 * (`floatingLabels.ts`), each over the head of the piece it would capture.
 */
export function createHighlights(
  scene: Scene,
  board: Board,
  parent: TransformNode | null = null,
): Highlights {
  const unlit = (name: string, color: Color3) => {
    const material = new StandardMaterial(name, scene);
    material.disableLighting = true;
    material.emissiveColor = color;
    return material;
  };

  const rings = MeshBuilder.CreateTorus(
    'targetRings',
    { diameter: TILE_SIZE * 0.78, thickness: TILE_SIZE * 0.06, tessellation: 24 },
    scene,
  );
  const ringMaterial = unlit('targetRingMaterial', Color3.White());
  rings.material = ringMaterial;
  rings.isPickable = false;
  rings.parent = parent;
  rings.thinInstanceSetBuffer('matrix', new Float32Array(0), 16, false);
  rings.thinInstanceSetBuffer('color', new Float32Array(0), 4, false);
  rings.isVisible = false;

  /**
   * A ring with a dark band under it, wider on both sides: one mesh, one draw call. The light
   * ring shows on a dark square and the band outlines it on a light one, where a white ring
   * alone all but vanished, the gamepad's cursor with it (client-board:quality#10).
   */
  const rimmedRing = (name: string, diameter: number, thickness: number, color: string) => {
    const paint = (mesh: Mesh, hex: string) => {
      const c = Color3.FromHexString(hex);
      const colors = new Float32Array(mesh.getTotalVertices() * 4);
      for (let i = 0; i < colors.length; i += 4) colors.set([c.r, c.g, c.b, 1], i);
      mesh.setVerticesData(VertexBuffer.ColorKind, colors);
    };
    const band = MeshBuilder.CreateTorus(
      `${name}Band`,
      { diameter, thickness: thickness * 1.9, tessellation: 24 },
      scene,
    );
    // Flat and a little lower, so the light ring lies on it rather than inside it.
    band.scaling.y = 0.2;
    band.position.y = -thickness * 0.3;
    paint(band, COLORS.ringBand);
    const ring = MeshBuilder.CreateTorus(name, { diameter, thickness, tessellation: 24 }, scene);
    paint(ring, color);
    const merged = Mesh.MergeMeshes([band, ring], true)!;
    merged.name = name;
    return merged;
  };

  const selection = rimmedRing(
    'selectionRing',
    TILE_SIZE * 0.9,
    TILE_SIZE * 0.05,
    COLORS.selection,
  );
  // White: the colour is the vertices'.
  const selectionMaterial = unlit('selectionRingMaterial', Color3.White());
  selection.material = selectionMaterial;
  selection.isPickable = false;
  selection.parent = parent;
  selection.isVisible = false;

  // Just outside the target rings, thinner than the selection: what a click or A would pick.
  const hover = rimmedRing('hoverRing', TILE_SIZE * 0.96, TILE_SIZE * 0.03, COLORS.hover);
  const hoverMaterial = unlit('hoverRingMaterial', Color3.White());
  hoverMaterial.alpha = 0.85;
  hover.material = hoverMaterial;
  hover.isPickable = false;
  hover.parent = parent;
  hover.isVisible = false;

  // Inside the target rings and clear of a piece's base, so a threatened piece can also be
  // selected or be a spell target without the rings merging.
  const warnings = MeshBuilder.CreateTorus(
    'threatRings',
    { diameter: TILE_SIZE * 0.66, thickness: TILE_SIZE * 0.028, tessellation: 24 },
    scene,
  );
  const warningColor = Color3.FromHexString(COLORS.threat);
  const warningMaterial = unlit('threatRingMaterial', warningColor.clone());
  warnings.material = warningMaterial;
  warnings.isPickable = false;
  warnings.parent = parent;
  warnings.thinInstanceSetBuffer('matrix', new Float32Array(0), 16, false);
  warnings.isVisible = false;

  // Under an afflicted piece, wider than a warning ring so both can show at once, in the pale
  // ice of the sign overhead: the sign says what, the ring says where.
  const afflictions = MeshBuilder.CreateTorus(
    'afflictionRings',
    { diameter: TILE_SIZE * 0.8, thickness: TILE_SIZE * 0.022, tessellation: 24 },
    scene,
  );
  const afflictionMaterial = unlit('afflictionRingMaterial', Color3.FromHexString('#dff3ff'));
  afflictions.material = afflictionMaterial;
  afflictions.isPickable = false;
  afflictions.parent = parent;
  afflictions.thinInstanceSetBuffer('matrix', new Float32Array(0), 16, false);
  afflictions.isVisible = false;

  // The last move's squares, lit faintly under everything else: one mesh of thin instances, one
  // draw call. A player who looked away, at the tablets or round the room, sees what moved and
  // from where (x-aaa-gap#18).
  const lastMove = MeshBuilder.CreateDisc(
    'lastMoveSquares',
    { radius: TILE_SIZE * 0.46, tessellation: 32 },
    scene,
  );
  lastMove.rotation.x = Math.PI / 2;
  lastMove.bakeCurrentTransformIntoVertices();
  const lastMoveMaterial = unlit(
    'lastMoveMaterial',
    Color3.FromHexString(COLORS.highlightLastMove),
  );
  lastMoveMaterial.alpha = LAST_MOVE_ALPHA;
  lastMove.material = lastMoveMaterial;
  lastMove.isPickable = false;
  lastMove.parent = parent;
  lastMove.thinInstanceSetBuffer('matrix', new Float32Array(0), 16, false);
  lastMove.isVisible = false;

  /** The odds over attack targets: one sheet of labels, one draw call (`floatingLabels.ts`). */
  const labels = createFloatingLabels<number>(scene, {
    name: 'attackOddsLabels',
    parent,
    atlas: { width: ATLAS_WIDTH, height: ATLAS_HEIGHT },
    slot: { width: SLOT_WIDTH, height: SLOT_HEIGHT },
    size: { width: LABEL_WIDTH, height: LABEL_HEIGHT },
    draw: (ctx, rect, odds) => drawLabel(ctx, rect, odds),
    grow: OVERHEAD_GROW,
  });
  let labelTexts: string[] = [];

  let palette: Palette = PALETTES.classic;
  /** What is lit right now, so a new palette can repaint it. */
  let showing: {
    targets: RingTarget[];
    pieceCells: { x: number; y: number }[];
    standing?: (x: number, y: number) => Standing | undefined;
  } | null = null;

  const colorFor = (t: RingTarget) => {
    if (t.kind === 'attack' && t.odds !== undefined) {
      const { r, g, b } = oddsColor(t.odds, palette.odds);
      return new Color3(r, g, b);
    }
    return Color3.FromHexString(
      t.kind === 'attack'
        ? palette.highlights.attack
        : t.kind === 'spell'
          ? palette.highlights.spell
          : palette.highlights.move,
    );
  };

  const placeRings = (mesh: Mesh, cells: { x: number; y: number }[], y: number) => {
    if (cells.length === 0) {
      mesh.isVisible = false;
      return;
    }
    const matrices = new Float32Array(cells.length * 16);
    cells.forEach((c, i) => {
      const p = cellToWorld(board, c.x, c.y);
      Matrix.Translation(p.x, y, p.z).copyToArray(matrices, i * 16);
    });
    mesh.thinInstanceSetBuffer('matrix', matrices, 16, false);
    mesh.isVisible = true;
  };

  const drawLabel = (ctx: CanvasRenderingContext2D, rect: SlotRect, odds: number) => {
    const { x, y } = rect;
    const color = oddsCss(odds, palette.odds);
    roundedRect(ctx, { x: x + 4, y: y + 4, w: rect.w - 8, h: rect.h - 8 }, 16);
    ctx.fillStyle = 'rgba(12, 9, 18, 0.82)';
    ctx.fill();
    ctx.lineWidth = 4;
    ctx.strokeStyle = color;
    ctx.stroke();
    ctx.font = `700 38px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 5;
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.strokeText(oddsLabel(odds), x + rect.w / 2, y + rect.h / 2 + 2);
    ctx.fillStyle = color;
    ctx.fillText(oddsLabel(odds), x + rect.w / 2, y + rect.h / 2 + 2);
  };

  const setLabels = (
    targets: RingTarget[],
    standing: ((x: number, y: number) => Standing | undefined) | undefined,
  ) => {
    const labelled = targets
      .filter((t) => t.kind === 'attack' && t.odds !== undefined)
      .slice(0, SLOT_COUNT);
    labelTexts = labelled.map((t) => oddsLabel(t.odds!));
    // The cells are in the board's own space and the sheet places labels in the world's.
    const toWorld = parent ? parent.computeWorldMatrix(true) : Matrix.IdentityReadOnly;
    labels.set(
      labelled.map((t) => {
        // Over the head of the piece it would capture, and over its sign when it wears one, lifted
        // along the eye's up so neither hides the other. A fixed 1.2 tiles over the square sat on
        // the heads of the tallest pieces.
        const on = standing?.(t.x, t.y);
        const p = cellToWorld(board, t.x, t.y);
        p.y += on?.top ?? DEFAULT_PIECE_TOP;
        return {
          at: Vector3.TransformCoordinates(p, toWorld),
          item: t.odds!,
          lift: (on?.signed ? SIGN_REACH : 0) + OVERHEAD_GAP + LABEL_HEIGHT / 2,
        };
      }),
    );
  };

  let clock = 0;

  const setTargets = (
    targets: RingTarget[],
    pieceCells: { x: number; y: number }[],
    standing?: (x: number, y: number) => Standing | undefined,
  ) => {
    showing = { targets, pieceCells, standing };
    const all: RingTarget[] = [
      ...targets,
      ...pieceCells.map((c) => ({ x: c.x, y: c.y, kind: 'spell' as const })),
    ];
    setLabels(targets, standing);
    if (all.length === 0) {
      rings.isVisible = false;
      return;
    }
    const matrices = new Float32Array(all.length * 16);
    const colors = new Float32Array(all.length * 4);
    all.forEach((t, i) => {
      const p = cellToWorld(board, t.x, t.y);
      Matrix.Translation(p.x, BOARD_TOP_Y + 0.003, p.z).copyToArray(matrices, i * 16);
      const c = colorFor(t);
      colors.set([c.r, c.g, c.b, 1], i * 4);
    });
    rings.thinInstanceSetBuffer('matrix', matrices, 16, false);
    rings.thinInstanceSetBuffer('color', colors, 4, false);
    rings.isVisible = true;
  };

  return {
    setTargets,
    setPalette(next) {
      palette = next;
      lastMoveMaterial.emissiveColor = Color3.FromHexString(next.highlights.lastMove);
      // The labels are drawn in the palette's odds colours: paint them again, though they say the same.
      labels.invalidate();
      if (showing) setTargets(showing.targets, showing.pieceCells, showing.standing);
    },
    setSelection(cell) {
      if (!cell) {
        selection.isVisible = false;
        return;
      }
      const p = cellToWorld(board, cell.x, cell.y);
      selection.position.set(p.x, BOARD_TOP_Y + 0.004, p.z);
      selection.isVisible = true;
    },
    setHover(cell) {
      if (!cell) {
        hover.isVisible = false;
        return;
      }
      const p = cellToWorld(board, cell.x, cell.y);
      hover.position.set(p.x, BOARD_TOP_Y + 0.0045, p.z);
      hover.isVisible = true;
    },
    setThreats(cells) {
      placeRings(warnings, cells, BOARD_TOP_Y + 0.0035);
    },
    setAfflicted(cells) {
      placeRings(afflictions, cells, BOARD_TOP_Y + 0.003);
    },
    setLastMove(cells) {
      placeRings(lastMove, cells, BOARD_TOP_Y + 0.0015);
    },
    update(dt, camera) {
      clock += dt;
      if (warnings.isVisible)
        warningMaterial.emissiveColor = warningColor.scale(0.7 + 0.3 * Math.sin(clock * 4));
      if (camera) labels.face(camera);
    },
    labels: () => labelTexts.slice(),
    dispose() {
      rings.dispose();
      selection.dispose();
      hover.dispose();
      warnings.dispose();
      afflictions.dispose();
      lastMove.dispose();
      lastMoveMaterial.dispose();
      labels.dispose();
      ringMaterial.dispose();
      selectionMaterial.dispose();
      hoverMaterial.dispose();
      warningMaterial.dispose();
      afflictionMaterial.dispose();
    },
  };
}
